import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResourceTable } from "@/components/data/ResourceTable";
import { OptionSelect, RefSelect } from "@/components/data/FieldInput";
import { SimpleTable } from "@/components/data/SimpleTable";
import { PageHeader } from "@/components/data/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtMoney, monthStart, today } from "@/lib/api";

const MOYEN = { name: "m", type: "refitem", ref: "moyen_paiement" };
const COMPTE = { name: "c", type: "ref", ref: "comptes" };
const NIVEAUX = [["journee", "Par journée"], ["tpe", "Par TPE"], ["revendeur", "Par revendeur"], ["superviseur", "Par superviseur"], ["jeu", "Par type de jeu"]].map(([value, label]) => ({ value, label }));

function PayDialog({ gain, onClose, onDone }) {
  const [f, setF] = useState({ compte_id: null, moyen_paiement_id: null, date: today() });
  const pay = async () => {
    try {
      await api.post(`/gains/${gain.id}/payer`, f);
      toast.success("Gain payé");
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gain-pay-dialog">
        <DialogHeader><DialogTitle>Payer le gain {gain.numero} — {fmtMoney(gain.montant)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Compte de paiement *</Label><RefSelect field={COMPTE} value={f.compte_id} onChange={(v) => setF({ ...f, compte_id: v })} testId="gain-pay-compte" /></div>
          <div><Label>Moyen de paiement *</Label><RefSelect field={MOYEN} value={f.moyen_paiement_id} onChange={(v) => setF({ ...f, moyen_paiement_id: v })} testId="gain-pay-moyen" /></div>
          <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={pay} disabled={!f.compte_id || !f.moyen_paiement_id} data-testid="gain-pay-submit">Confirmer le paiement</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Recap() {
  const [p, setP] = useState({ date_from: monthStart(), date_to: today(), niveau: "journee" });
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/gains/recap", { params: p }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [p]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="w-full sm:w-52"><OptionSelect options={NIVEAUX} value={p.niveau} onChange={(v) => v && setP({ ...p, niveau: v })} testId="gains-recap-niveau" /></div>
        <Input type="date" className="h-9 w-[150px]" value={p.date_from} onChange={(e) => setP({ ...p, date_from: e.target.value })} />
        <Input type="date" className="h-9 w-[150px]" value={p.date_to} onChange={(e) => setP({ ...p, date_to: e.target.value })} />
      </div>
      <SimpleTable title="Récapitulatif des gains" module="gains" testId="gains-recap-table" rows={rows} cols={[
        { k: "libelle", label: "Groupe" }, { k: "importes", label: "Importés (TFJ)", type: "money" }, { k: "declares", label: "Déclarés", type: "money" },
        { k: "valides", label: "Validés (non payés)", type: "money" }, { k: "payes", label: "Payés", type: "money" },
        { k: "ecart", label: "Écart validés+payés − importés", type: "money" }]} />
      <p className="text-[11px] text-muted-foreground">Importés : gains remontés par les TPE dans le TFJ. Déclarés / validés / payés : gains saisis dans ce module. Les écarts sont signalés sans correction automatique.</p>
    </div>
  );
}

export default function GainsPage() {
  const { can } = useAuth();
  const [pay, setPay] = useState(null);
  const [reload, setReload] = useState(0);
  const extra = (row) => [{ key: "payer", label: "Payer", icon: Banknote, show: can("gains", "valider") && row.statut === "valide" && row.statut_paiement !== "paye", onClick: setPay }];
  return (
    <div>
      <PageHeader title="Gains" subtitle="Gains déclarés (brouillon) → validés → payés, comparés aux gains importés du TFJ. Chaque validation et paiement est historisé." />
      <Tabs defaultValue="liste">
        <TabsList className="mb-4"><TabsTrigger value="liste" data-testid="tab-gains-liste">Gains déclarés</TabsTrigger><TabsTrigger value="recap" data-testid="tab-gains-recap">Récapitulatif</TabsTrigger></TabsList>
        <TabsContent value="liste"><ResourceTable res="gains" extraActions={extra} reloadKey={reload} /></TabsContent>
        <TabsContent value="recap"><Recap /></TabsContent>
      </Tabs>
      {pay && <PayDialog gain={pay} onClose={() => setPay(null)} onDone={() => setReload((n) => n + 1)} />}
    </div>
  );
}
